// Selectable tree levels for the Google Ads dashboard — PURE + tested.
//
// The Google analog of the Meta breakdown's group-by dimensions: the user turns
// levels on and off and the tree nests over only the ACTIVE ones, in canonical
// order (Location → Campaign → Ad Group → Ad). Shared by the client chips and the
// server rollup so both agree on ordering, the at-least-one rule, and which SQL
// grain a selection needs — a disagreement there would show the user one nesting
// and compute another.
//
// ⚠️ LOCATION IS A KYCK CONCEPT, not a Google one (Phase 2c). It is resolved
// server-side from the attribution directory, which is why the leaf SQL grain for
// a Location-only selection is `campaign` — Google's deepest column below a
// Location IS the campaign.

import type { GoogleAdsLevel } from '../types/googleAdsReporting.js';

/** Canonical nesting order, shallowest → deepest. */
export const GOOGLE_ADS_LEVELS: GoogleAdsLevel[] = ['location', 'campaign', 'adGroup', 'ad'];

export const GOOGLE_ADS_LEVEL_LABEL: Record<GoogleAdsLevel, string> = {
  location: 'Location',
  campaign: 'Campaign',
  adGroup: 'Ad group',
  ad: 'Ad',
};

/** The selection a fresh dashboard opens with — the by-client, by-campaign view. */
export const DEFAULT_GOOGLE_ADS_LEVELS: GoogleAdsLevel[] = ['location', 'campaign'];

/**
 * Restore canonical order and drop unknowns/duplicates.
 *
 * ⚠️ NEVER returns an empty list: an empty selection would render a table with no
 * rows and no explanation, which reads as broken rather than as a choice. An
 * empty/garbage input falls back to the default — the same posture as
 * `sanitizeMetricKeys` and Meta's `canonicalizeGroupBy`.
 */
export function canonicalizeLevels(levels: readonly string[]): GoogleAdsLevel[] {
  const wanted = new Set(levels);
  const ordered = GOOGLE_ADS_LEVELS.filter((l) => wanted.has(l));
  return ordered.length > 0 ? ordered : [...DEFAULT_GOOGLE_ADS_LEVELS];
}

/**
 * Toggle one level, keeping canonical order and refusing to turn the last one
 * off (the UI disables it too, but the rule lives here so the server can't be
 * handed an empty selection either).
 */
export function toggleLevel(
  levels: readonly GoogleAdsLevel[],
  level: GoogleAdsLevel,
): GoogleAdsLevel[] {
  const active = levels.includes(level);
  if (active && levels.length === 1) return [...levels];
  return canonicalizeLevels(
    active ? levels.filter((l) => l !== level) : [...levels, level],
  );
}

/** The deepest active level — the drill floor (nothing nests below it). */
export function deepestLevel(levels: readonly GoogleAdsLevel[]): GoogleAdsLevel {
  const canonical = canonicalizeLevels(levels);
  return canonical[canonical.length - 1]!;
}

/** Position in the canonical order (0 = Location). */
export function levelDepth(level: GoogleAdsLevel): number {
  return GOOGLE_ADS_LEVELS.indexOf(level);
}

/**
 * The levels a table shows BELOW the current focus. Focusing a Location with
 * [Location, Campaign, Ad Group] active leaves [Campaign, Ad Group] to nest —
 * the focused entity is the breadcrumb, not a row repeated at the top of its own
 * children.
 *
 * Empty when the focus is already at the deepest active level, which is exactly
 * why that level is the drill floor: `canDrillInto` refuses to focus there.
 */
export function visibleLevelsForFocus(
  levels: readonly GoogleAdsLevel[],
  focusLevel: GoogleAdsLevel | null,
): GoogleAdsLevel[] {
  const canonical = canonicalizeLevels(levels);
  if (!focusLevel) return canonical;
  return canonical.filter((l) => levelDepth(l) > levelDepth(focusLevel));
}

/**
 * Can a row at this level be drilled into? Only when an active level nests below
 * it — otherwise focusing would blank the table while re-scoping everything else,
 * which reads as a bug rather than a navigation.
 */
export function canDrillInto(
  levels: readonly GoogleAdsLevel[],
  level: GoogleAdsLevel,
): boolean {
  return visibleLevelsForFocus(levels, level).length > 0;
}

/**
 * The SQL grain the leaf rows must be fetched at for a given selection.
 *
 * ⚠️ `location` has no SQL grain of its own — it is assembled server-side from the
 * attribution directory — so a Location-only selection still fetches CAMPAIGN
 * rows and rolls them up. Everything else takes the deepest active Google level.
 */
export function leafSqlLevelFor(
  levels: readonly GoogleAdsLevel[],
): 'campaign' | 'adGroup' | 'ad' {
  const canonical = canonicalizeLevels(levels);
  if (canonical.includes('ad')) return 'ad';
  if (canonical.includes('adGroup')) return 'adGroup';
  return 'campaign';
}

/**
 * ⚠️ Does this selection fetch at a grain DEEPER than the campaign?
 *
 * It matters because Google does not report every campaign type at every grain:
 * Performance Max campaigns have NO ad groups and NO ads (they use asset groups),
 * so a selection including Ad Group or Ad silently excludes all PMAX spend from
 * the tree. Measured on the live book: Search has 100% ad-level cost coverage,
 * PMAX has 0% — $2,977 of $60,082 would simply not appear.
 *
 * This is the same consequence the Meta rollup documents ("the source grain
 * deepens as you add dimensions, so adding the Ad dimension can slightly reduce
 * coarser totals"), but far more concentrated: entire campaigns vanish rather
 * than totals shrinking a little. So the server reports what was excluded and
 * the UI says so, instead of letting the number quietly drop.
 */
export function fetchesBelowCampaign(levels: readonly GoogleAdsLevel[]): boolean {
  return leafSqlLevelFor(levels) !== 'campaign';
}
